import { useState } from "react";
import { Modal, Select, Form, Button, Spin } from "antd";
import { useQuery } from "react-query";
import { getFaculties } from "../../../Requests/faculty-requests";
import { getStudyPrograms } from "../../../Requests/study-program-request";
import { getAllStudyPlans } from "../../../Requests/study-plan-requests";
import { getShortSubjects } from "../../../Requests/subject-requests";
import { Faculty, StudyProgramDTO, StudyPlanDTO, SubjectDTO, StudyCycleEnum, StudyProgramQueryParams } from "../../../Api";
import { theme } from "../../../theme";

const { Option } = Select;

interface ImportSubjectModalProps {
  visible: boolean;
  onCancel: () => void;
  onConfirm: (subjectId: string) => void;
  studyCycle?: StudyCycleEnum;
}

const ImportSubjectModal = ({ visible, onCancel, onConfirm, studyCycle }: ImportSubjectModalProps) => {
  const [form] = Form.useForm();
  const [selectedFacultyId, setSelectedFacultyId] = useState<string | undefined>(undefined);
  const [selectedProgramId, setSelectedProgramId] = useState<string | undefined>(undefined);
  const [selectedPlanId, setSelectedPlanId] = useState<string | undefined>(undefined);
  const [selectedSubjectId, setSelectedSubjectId] = useState<string | undefined>(undefined);

  const { data: faculties, isLoading: isLoadingFaculties } = useQuery(
    ['getFacultiesForImport'],
    () => getFaculties(undefined, undefined, 1, 100),
    {
      enabled: visible,
    }
  );

  const { data: programs, isLoading: isLoadingPrograms } = useQuery(
    ['getProgramsForImport', selectedFacultyId, studyCycle],
    () => {
      if (!selectedFacultyId || !studyCycle) return { data: [] };
      const params: StudyProgramQueryParams = {
        page: 1,
        pageSize: 100,
        facultyIds: [selectedFacultyId],
        studyCycle: studyCycle,
        universityCenterId: '00000000-0000-0000-0000-000000000000',
        domainIds: ['00000000-0000-0000-0000-000000000000'],
      };
      return getStudyPrograms(params);
    },
    {
      enabled: visible && !!selectedFacultyId && !!studyCycle,
    }
  );

  const { data: plans, isLoading: isLoadingPlans } = useQuery(
    ['getPlansForImport', selectedProgramId],
    () => {
      if (!selectedProgramId) return { data: [] };
      return getAllStudyPlans(selectedProgramId, 1, 100);
    },
    {
      enabled: visible && !!selectedProgramId,
    }
  );

  const { data: subjects, isLoading: isLoadingSubjects } = useQuery(
    ['getSubjectsForImport', selectedPlanId],
    () => {
      if (!selectedPlanId || !selectedProgramId) return { data: [] };
      const plan = plans?.data?.find(p => p.id === selectedPlanId);
      if (!plan) return { data: [] };
      return getShortSubjects(undefined, undefined, plan.planYear, undefined, undefined, selectedProgramId, 1, 500);
    },
    {
      enabled: visible && !!selectedPlanId && !!selectedProgramId,
    }
  );

  const handleFacultyChange = (facultyId: string) => {
    setSelectedFacultyId(facultyId);
    setSelectedProgramId(undefined);
    setSelectedPlanId(undefined);
    setSelectedSubjectId(undefined);
    form.setFieldsValue({
      programId: undefined,
      planId: undefined,
      subjectId: undefined,
    });
  };

  const handleProgramChange = (programId: string) => {
    setSelectedProgramId(programId);
    setSelectedPlanId(undefined);
    setSelectedSubjectId(undefined);
    form.setFieldsValue({
      planId: undefined,
      subjectId: undefined,
    });
  };

  const handlePlanChange = (planId: string) => {
    setSelectedPlanId(planId);
    setSelectedSubjectId(undefined);
    form.setFieldsValue({
      subjectId: undefined,
    });
  };

  const handleSubjectChange = (subjectId: string) => {
    setSelectedSubjectId(subjectId);
  };

  const handleConfirm = () => {
    if (selectedSubjectId) {
      onConfirm(selectedSubjectId);
      handleCancel();
    }
  };

  const handleCancel = () => {
    form.resetFields();
    setSelectedFacultyId(undefined);
    setSelectedProgramId(undefined);
    setSelectedPlanId(undefined);
    setSelectedSubjectId(undefined);
    onCancel();
  };

  return (
    <Modal
      open={visible}
      onCancel={handleCancel}
      title="Importă disciplină"
      width={600}
      footer={[
        <Button 
          key="cancel" 
          onClick={handleCancel}
          style={{ 
            background: theme.green, 
            color: theme.white, 
            borderRadius: '10px', 
            boxShadow: 'rgba(0, 0, 0, 0.1) 0px 4px 12px',
            borderColor: theme.green
          }}
        >
          Anulează
        </Button>,
        <Button
          key="confirm"
          type="primary"
          onClick={handleConfirm}
          disabled={!selectedSubjectId}
          style={{ 
            background: theme.secondColor, 
            color: theme.white,
            borderColor: theme.secondColor, 
            borderRadius: '10px',
            boxShadow: 'rgba(0, 0, 0, 0.1) 0px 4px 12px'
          }}
        >
          Confirmă
        </Button>,
      ]}
    >
      <div style={{ color: '#ff4d4f', fontWeight: 'bold', marginBottom: 16 }}>
        Atenție: Orice informație deja completată în secțiunile 4 - 10 va fi suprascrisă.
      </div>
      <Form form={form} layout="vertical">
        <Form.Item label="Facultate" name="facultyId" rules={[{ required: true, message: "Selectați facultatea" }]}>
          <Select
            placeholder="Selectați facultatea"
            onChange={handleFacultyChange}
            loading={isLoadingFaculties}
            showSearch
            filterOption={(input, option) => {
              const label = typeof option?.children === 'string' ? option.children : String(option?.children || '');
              return label.toLowerCase().indexOf(input.toLowerCase()) >= 0;
            }}
          >
            {faculties?.map((faculty: Faculty) => (
              <Option key={faculty.id} value={faculty.id}>
                {faculty.name}
              </Option>
            ))}
          </Select>
        </Form.Item>

        <Form.Item
          label="Program"
          name="programId"
          rules={[{ required: true, message: "Selectați programul" }]}
        >
          <Select
            placeholder="Selectați programul"
            onChange={handleProgramChange}
            loading={isLoadingPrograms}
            disabled={!selectedFacultyId}
            showSearch
            filterOption={(input, option) => {
              const label = typeof option?.children === 'string' ? option.children : String(option?.children || '');
              return label.toLowerCase().indexOf(input.toLowerCase()) >= 0;
            }}
          >
            {programs?.data?.map((program: StudyProgramDTO) => (
              <Option key={program.id} value={program.id}>
                {program.name}
              </Option>
            ))}
          </Select>
        </Form.Item>

        <Form.Item label="Plan" name="planId" rules={[{ required: true, message: "Selectați planul" }]}>
          <Select
            placeholder="Selectați planul"
            onChange={handlePlanChange}
            loading={isLoadingPlans}
            disabled={!selectedProgramId}
            showSearch
            filterOption={(input, option) => {
              const label = typeof option?.children === 'string' ? option.children : String(option?.children || '');
              return label.toLowerCase().indexOf(input.toLowerCase()) >= 0;
            }}
          >
            {plans?.data?.map((plan: StudyPlanDTO) => (
              <Option key={plan.id} value={plan.id}>
                {plan.planYear && plan.endPlanYear ? `${plan.planYear}-${plan.endPlanYear}` : `${plan.planYear}`}
              </Option>
            ))}
          </Select>
        </Form.Item>

        <Form.Item
          label="Disciplină"
          name="subjectId"
          rules={[{ required: true, message: "Selectați disciplina" }]}
        >
          <Select
            placeholder="Selectați disciplina"
            onChange={handleSubjectChange}
            loading={isLoadingSubjects}
            disabled={!selectedPlanId}
            showSearch
            filterOption={(input, option) => {
              const label = typeof option?.children === 'string' ? option.children : String(option?.children || '');
              return label.toLowerCase().indexOf(input.toLowerCase()) >= 0;
            }}
          >
            {subjects?.data?.map((subject: SubjectDTO) => (
              <Option key={subject.id} value={subject.id}>
                {subject.nameRO || subject.nameEN || subject.code || subject.id}
              </Option>
            ))}
          </Select>
        </Form.Item>
      </Form>
    </Modal>
  );
};

export default ImportSubjectModal;
